import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useKeycloak } from 'react-keycloak';
import { useTranslation } from 'react-i18next';
import { Link, navigate } from 'gatsby';
import DOMPurify from 'dompurify';
import { useQuery } from '@apollo/react-hooks';
import { Slide, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { UserAccountWrapper, UserAccountLabel } from './styles/UserAccountStyles';
import { HighlightedText } from '../../Common/styled/HighlightedText';
import { isEmpty } from '../../../utils/ObjectUtils';
import { useApplicationInterface } from '../../../utils/ApplicationInterfaceUtils';
import { GET_USER_BILLING_PLAN, GET_USER_FOR_PREVIEW } from '../../../graphql/queries';
import UserAccountPopup from './UserAccountPopup';

const PLAN_STORAGE_STATUS_OK = 0;
const PLAN_STORAGE_STATUS_WARNING = 1;
const PLAN_STORAGE_STATUS_EXCEEDED = 2;

const getPlanUsage = (plan): number => {
  if (!plan) return PLAN_STORAGE_STATUS_OK;
  if (!plan.userBillingPlan) return PLAN_STORAGE_STATUS_OK;
  if (!plan.userBillingPlan.billingPlan) return PLAN_STORAGE_STATUS_OK;

  const consumed = plan.userBillingPlan.consumedStorage;
  const limit = plan.userBillingPlan.billingPlan.storage;
  const ratio = consumed / limit;

  if (ratio > 1) return PLAN_STORAGE_STATUS_EXCEEDED;
  if (ratio > .9) return PLAN_STORAGE_STATUS_WARNING;

  return PLAN_STORAGE_STATUS_OK;
}

function TransitionRight(props) {
  return <Slide {...props} direction="right" />;
}

export default () => {
  const { t } = useTranslation();
  const { storageAlert } = useSelector((state) => state.userInterface);
  const dispatch = useDispatch();
  const { loading, data } = useQuery(GET_USER_FOR_PREVIEW);
  const { data: plan } = useQuery(GET_USER_BILLING_PLAN);
  const [keycloak] = useKeycloak();
  const [transition, setTransition] = useState();

  const handleClose = () => {
    setTransition(() => TransitionRight);
    dispatch({ type: 'SET_STORAGE_ALERT', storageAlert: { storageAlert: { ...storageAlert, open: false } } });
  }

  const logout = () => {
    sessionStorage.clear();
    localStorage.clear();
    keycloak.logout({ redirectUri: process.env.GATSBY_APP_BASE_URL });
  };

  const linkTo = (uri) => {
    navigate(uri);
  };

  const openUrl = ({ url, target, rel }) => {
    const redirect = window.open(url, target, rel);
    if (redirect) redirect.focus();
  };

  const usage = getPlanUsage(plan);

  useEffect(() => {
    if (usage === PLAN_STORAGE_STATUS_OK) return;
    if (usage === storageAlert.type) return;

    const delay = setTimeout(() => {
      setTransition(() => TransitionRight);
      dispatch({ type: 'SET_STORAGE_ALERT', storageAlert: { storageAlert: { open: true, type: usage } } });
    }, 5000);

    return () => clearTimeout(delay);
  }, [usage]);

  if (loading || isEmpty(data)) return null;

  const { patientApp } = useApplicationInterface();
  const planDescription = plan && plan.userBillingPlan ? plan.userBillingPlan.billingPlan.name : 'No plan';
  const userDescription = patientApp ? t('patient') : planDescription;

  const alert = usage === PLAN_STORAGE_STATUS_EXCEEDED ? {
    severity: 'error',
    message: t('storage.limit.exceed.please.upgrade.your.plan'),
  } : {
    severity: 'warning',
    message: t('limit.plan.alert.storage'),
  };

  return (
    <UserAccountWrapper>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={10}
        open={storageAlert.open}
        TransitionComponent={transition}
        key={transition ? transition.name : ''}
      >
        <Alert onClose={handleClose} severity={alert.severity}>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(alert.message) }} />
          <div>
            {t('you.can.check.your.plan.details')}
            <Link className="no-link" to={'/user/settings/plan'}>
              <b> {t('here')}</b>
            </Link>
          </div>
        </Alert>
      </Snackbar>
      <UserAccountLabel>
        {`${data.user.name} ${data.user.surname}`}
        <br />
        <HighlightedText>{userDescription}</HighlightedText>
      </UserAccountLabel>
      <UserAccountPopup linkTo={linkTo} openUrl={openUrl} logout={logout} />
    </UserAccountWrapper>
  );
};
